import _, {first, memoize, sortBy, take} from 'lodash';
import {createSelector} from 'reselect';

import {DropdownFetcher} from '../../../components/common/dropdowns/source/asyncDropdownSource';
import {isTracker, teammateCanAccessNamespace} from '../../../helpers/modelHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {filterMentionableTeammates} from '../../../helpers/models/teammateHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../models/api/conversationModel';
import {RecipientContactMatchModel} from '../../../models/api/recipientMatchModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {fetchRecipientContactMatchesAsync} from '../../../services/recipientMatches';
import {getCanAutocompleteMentionContacts, getCompany} from '../company/companySelectors';
import {
  ConversationBasesSelector,
  OptionalConversationSelector,
} from '../conversations/conversationsSelectors';
import {getTeammateInteractionScoresByTeammateId} from '../localPreferences/localPreferencesSelectors';
import {getFindTeammate} from './lazyTeammatesSelectors';
import {
  getFlatActiveTeammates,
  getFlatOtherActiveTeammates,
  getTeammatesInCommonTeamIds,
  getUserAlias,
  getUserId,
} from './teammatesSelectors';

/*
 * Global collections.
 */

const getTeammateAliases = wrapInShallowSelector(
  createSelector(getFlatActiveTeammates, (activeTeammates) => activeTeammates.map((t) => t.alias)),
);

/** Get a set of aliases for all teammates. */
export const getMentionableAliases = wrapInShallowSelector(
  createSelector(getTeammateAliases, (aliases) => new Set(['all', ...aliases])),
);

/** Get all aliases including the self. */
export const getAllAliases = createSelector(
  [getTeammateAliases, getUserAlias],
  (teammateAliases, userAlias) => new Set(['all', userAlias, ...teammateAliases]),
);

const getTeammateEmails = createSelector(getFlatActiveTeammates, (activeTeammates) =>
  activeTeammates.map((t) => t.email),
);

/** Get a set of emails for all teammates. */
export const getMentionableEmails = createSelector(
  wrapInShallowSelector(getTeammateEmails),
  (emails) => new Set(emails),
);

/*
 * Internal: find interactable teammates by conversation or namespace.
 */

export type GetConversations = AppParametricSelector<any, ReadonlyArray<ConversationModel>>;
export type GetConversationBases = AppParametricSelector<any, ReadonlyArray<ConversationBaseModel>>;
type NamespaceSelector<P> = AppParametricSelector<P, string>;
export type TeammatesSelector<P> = AppParametricSelector<P, ReadonlyArray<TeammateModel>>;

function makeGetInteractableTeammatesForConversations<P>(getConversations: ConversationBasesSelector<P>) {
  return createSelector(
    [getFlatOtherActiveTeammates, getTeammatesInCommonTeamIds, wrapInShallowSelector(getConversations)],
    (flatOtherActiveTeammates, teammatesInCommonTeamIds, conversations) => {
      // For performance reasons we deduplicate the namespaces to test.
      const namespaces = _(conversations)
        .map((c) => c.namespace)
        .uniq()
        .value();

      // Check if a teammate is a team member within the context of the specified conversations.
      const isTeamMember = memoize((teammate: TeammateModel) =>
        namespaces.every((namespace) => {
          if (isTeammateNamespace(namespace)) {
            return teammatesInCommonTeamIds.has(teammate.id);
          }

          return teammateCanAccessNamespace(teammate, namespace);
        }),
      );

      return sortBy(flatOtherActiveTeammates, (t) => !isTeamMember(t));
    },
  );
}

export function makeGetInteractableTeammatesForConversationsIncludingUser<P>(
  getConversations: ConversationBasesSelector<P>,
) {
  return createSelector(
    [getUserId, getFlatActiveTeammates, getTeammatesInCommonTeamIds, wrapInShallowSelector(getConversations)],
    (userId, flatActiveTeammates, teammatesInCommonTeamIds, conversations) => {
      // For performance reasons we deduplicate the namespaces to test.
      const namespaces = _(conversations)
        .map((c) => c.namespace)
        .uniq()
        .value();

      // Check if a teammate is a team member within the context of the specified conversations.
      const isTeamMember = (teammate: TeammateModel) =>
        namespaces.every((namespace) => {
          if (isTeammateNamespace(namespace)) {
            return teammatesInCommonTeamIds.has(teammate.id) || teammate.id === userId;
          }

          return teammateCanAccessNamespace(teammate, namespace);
        });

      return sortBy(flatActiveTeammates, (t) => !isTeamMember(t));
    },
  );
}

function makeGetInteractableTeammatesForNamespace<P>(getNamespace: NamespaceSelector<P>) {
  return createSelector(
    [getFlatOtherActiveTeammates, getTeammatesInCommonTeamIds, getNamespace],
    (flatOtherActiveTeammates, teammatesInCommonTeamIds, namespace) => {
      const isNamespacePrivate = isTeammateNamespace(namespace);

      // Check if a teammate is a team member within the context of the specified namespace.
      const isTeamMember = memoize((teammate: TeammateModel) => {
        if (isNamespacePrivate) {
          return teammatesInCommonTeamIds.has(teammate.id);
        }

        return teammateCanAccessNamespace(teammate, namespace);
      });

      return sortBy(flatOtherActiveTeammates, (t) => !isTeamMember(t));
    },
  );
}

function makeGetInternalMentionableTeammates<P>(getInteractableTeammates: TeammatesSelector<P>) {
  return createSelector(
    [getInteractableTeammates, getTeammateInteractionScoresByTeammateId],
    (interactableTeammates, scores) =>
      sortBy(interactableTeammates, (t) => {
        const score = scores[t.id] || 0;
        return -score;
      }),
  );
}

/*
 * Public API: find mentionable and assignable teammates.
 */

export function makeGetMentionableTeammatesForConversations<P>(
  getConversations: ConversationBasesSelector<P>,
) {
  const getInteractableTeammates = makeGetInteractableTeammatesForConversations(getConversations);
  return makeGetInternalMentionableTeammates(getInteractableTeammates);
}

export function makeGetMentionableTeammatesForNamespace<P>(getNamespace: NamespaceSelector<P>) {
  const getInteractableTeammates = makeGetInteractableTeammatesForNamespace(getNamespace);
  return makeGetInternalMentionableTeammates(getInteractableTeammates);
}

export function makeGetAssignableTeammatesForConversation<P>(getConversations: ConversationBasesSelector<P>) {
  const getInteractableTeammates = makeGetInteractableTeammatesForConversations(getConversations);
  const getMentionableTeammates = makeGetInternalMentionableTeammates(getInteractableTeammates);

  return createSelector(
    [getMentionableTeammates, getInteractableTeammates],
    (mentionableTeammates, interactableTeammates) =>
      _(mentionableTeammates).take(5).concat(interactableTeammates).uniq().value(),
  );
}

export function makeGetIsTeamMemberForConversations<P>(getConversations: ConversationBasesSelector<P>) {
  return createSelector([getConversations], (conversations) => {
    // If more than one conversation is selected, consider any team member as part of the team so we
    // don't show the "Not in team" pill in teammate dropdowns.
    if (conversations.length > 1) {
      return () => true;
    }

    const conversation = first(conversations);
    if (!conversation) {
      return () => true;
    }

    const isPrivate = isTeammateNamespace(conversation.namespace);

    return memoize((teammate: TeammateModel) => {
      // Shortcut for the private case.
      if (isPrivate) {
        return true;
      }

      // Otherwise, check access.
      return teammateCanAccessNamespace(teammate, conversation.namespace);
    });
  });
}

export function makeGetIsTeamMemberForNamespace<P>(getNamespace: NamespaceSelector<P>) {
  return createSelector(getNamespace, (namespace) => {
    const isPrivate = isTeammateNamespace(namespace);

    return memoize((teammate: TeammateModel) => {
      // Shortcut for the private case.
      if (isPrivate) {
        return true;
      }

      // Otherwise, check access.
      return teammateCanAccessNamespace(teammate, namespace);
    });
  });
}

export function makeGetConversationParticipants<P>(getConversation: OptionalConversationSelector<P>) {
  return createSelector(
    [getConversation, getUserId, getFindTeammate],
    (conversation, userId, findTeammate) => {
      if (!conversation) {
        return [];
      }

      return _(conversation.trackers)
        .filter((t) => isTracker(t))
        .compact()
        .sortBy((t) => t.teammateId !== userId)
        .map((t) => t.teammateId)
        .map((id) => findTeammate(id, 'makeGetConversationParticipants'))
        .compact()
        .value();
    },
  );
}

/*
 * Shared: Fetch mentions.
 */

export function makeGetMentionsFetcher<P>(
  getMentionableTeammatesForConversations: TeammatesSelector<P>,
  resultsLimit: number,
  maxContactResults: number,
) {
  return createSelector(
    [
      getCompany,
      getMentionableEmails,
      getMentionableTeammatesForConversations,
      getCanAutocompleteMentionContacts,
    ],
    (
        company,
        mentionableEmails,
        mentionableTeammates,
        canAutocompleteMentionContacts,
      ): DropdownFetcher<TeammateModel | RecipientContactMatchModel> =>
      async (query, _items, cancelToken) => {
        // If the query is empty, just update the state.
        if (!query) {
          return {items: [], canLoadMore: false};
        }

        // If it's just the @ prefix, display the first X teammates.
        if (query === '@') {
          return {items: take(mentionableTeammates, resultsLimit), canLoadMore: false};
        }

        const aliasOrEmailQuery = query.slice(1);
        const matchingTeammates = filterMentionableTeammates(
          mentionableTeammates,
          aliasOrEmailQuery,
          resultsLimit,
        );

        // Check if we have enough teammates, or if we need contacts on top.
        if (matchingTeammates.length >= resultsLimit || !canAutocompleteMentionContacts) {
          return {
            items: matchingTeammates,
            canLoadMore: false,
          };
        }

        // Otherwise, fetch additional contacts. Take enough to reach the overall limit.
        const contactResultsLimit = Math.min(maxContactResults, resultsLimit - matchingTeammates.length);
        const contacts = await fetchRecipientContactMatchesAsync(company.url, aliasOrEmailQuery, {
          limit: contactResultsLimit,
          excludeEmails: mentionableEmails,
          cancelToken,
        });

        return {
          items: [...matchingTeammates, ...contacts],
          canLoadMore: false,
        };
      },
  );
}
